import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import {
    ActionPreviewChoiceOption,
    ActionPreviewChoiceSection,
    ActionPreviewEdits,
} from '../../../../../services/gable/gable.service';

/**
 * `choice` section — a low-confidence select rendered as a decision-surface
 * variant (A5) that GATES the primary confirm. When `suggested` is null Gable
 * can't tell, so nothing is preselected and the host keeps confirm disabled
 * (via its gating tooltip) until the user picks; when non-null it is
 * preselected and confirm is enabled.
 *
 * The header shows the label + the confidence badge (fed down from the card's
 * `why` section). Once a value is selected a live outcome line
 * (`effect_prefix` + selected option label) appears. Emits `(edit)` with
 * `{ value }`, which the host merges into `pendingEdits.choice`.
 */
@Component({
    selector: 'app-ap-choice-section',
    templateUrl: './choice-section.component.html',
    styleUrls: ['./choice-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule],
})
export class ActionPreviewChoiceSectionComponent {
    @Input({ required: true }) section!: ActionPreviewChoiceSection;
    @Input() pendingEdit: ActionPreviewEdits['choice'] | null = null;

    /** Confidence tier extracted from the card's `why` section; null → no badge. */
    @Input() confidence: 'high' | 'review' | 'low' | null = null;

    @Output() readonly edit = new EventEmitter<NonNullable<ActionPreviewEdits['choice']>>();

    /** Fixed badge copy per confidence tier; null when no badge should show. */
    get confidenceLabel(): string | null {
        switch (this.confidence) {
            case 'high':
                return 'High confidence';
            case 'review':
                return 'Needs your judgment';
            case 'low':
                return 'Needs your call';
            default:
                return null;
        }
    }

    /**
     * Live-outcome prefix. Middleware camelCases the nested preview payload, so
     * read `effectPrefix` first, snake as the fixture/back-compat fallback (P2).
     */
    get effectPrefix(): string | null {
        return this.section.effectPrefix ?? this.section.effect_prefix ?? null;
    }

    /** The current selection: user's pick if any, else the server suggestion (may be null). */
    get value(): string | null {
        return this.pendingEdit?.value ?? this.section.suggested;
    }

    /** Selected option label for the live outcome line; null when nothing chosen. */
    get selectedLabel(): string | null {
        const current = this.value;
        if (current === null) {
            return null;
        }
        return this.section.options.find((o) => o.value === current)?.label ?? null;
    }

    onSelectionChange(value: string): void {
        this.edit.emit({ value });
    }

    trackByOptionValue(_: number, option: ActionPreviewChoiceOption): string {
        return option.value;
    }
}
